/** Google Places accepts at most five country codes in one `componentRestrictions.country`. */
export const PLACES_MAX_COUNTRIES_PER_REQUEST = 5;

/** The one method of `google.maps.places.AutocompleteService` used here, callback form; a test stub satisfies it too. */
type PredictionService = {
  getPlacePredictions: (
    request: google.maps.places.AutocompletionRequest,
    callback: (results: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) => void
  ) => unknown;
};
type UnrestrictedRequest = Omit<google.maps.places.AutocompletionRequest, "componentRestrictions">;

const chunk = <T>(items: T[], size: number): T[][] =>
  items.reduce<T[][]>((groups, item, index) => {
    if (index % size === 0) groups.push([]);
    groups[groups.length - 1].push(item);
    return groups;
  }, []);

/**
 * `getPlacePredictions` restricted to `countries`, however many there are: one request per group of five
 * (Google's limit), run in parallel, merged in group order and deduped by `place_id`. An empty `countries`
 * resolves to `[]` without asking Google. `null` means no restriction — a single unrestricted request —
 * which the booking sheet uses only for an operator on behalf whose provider has the Worldwide Address
 * Search add-on. A provider's bookable list can legitimately be long (Royale's booking history spans ~28
 * countries), so the limit must not truncate it silently, which is what the first DEV-1606 cut did
 * (DEV-1612).
 */
export const getPlacePredictionsForCountries = (
  service: PredictionService,
  request: UnrestrictedRequest,
  countries: string[] | null
): Promise<google.maps.places.AutocompletePrediction[]> => {
  if (countries === null) {
    return new Promise((resolve) => {
      service.getPlacePredictions({ ...request }, (results) => resolve(results ?? []));
    });
  }
  if (countries.length === 0) return Promise.resolve([]);
  const groups = chunk(countries, PLACES_MAX_COUNTRIES_PER_REQUEST).map(
    (group) =>
      new Promise<google.maps.places.AutocompletePrediction[]>((resolve) => {
        service.getPlacePredictions({ ...request, componentRestrictions: { country: group } }, (results) => resolve(results ?? []));
      })
  );
  return Promise.all(groups).then((results) => {
    const seen = new Set<string>();
    return results.flat().filter((prediction) => {
      if (seen.has(prediction.place_id)) return false;
      seen.add(prediction.place_id);
      return true;
    });
  });
};
